import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, ArrowLeft, Check, X } from 'lucide-react';
// CSS handled by global theme.css
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function SupplierPaymentPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allVendors, setAllVendors] = useState([]);

  const [currentView, setCurrentView] = useState('list'); // 'list', 'form'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    supplier_name: '',
    bill_no: '',
    goods_purchase_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    cash_amount: '',
    bank_amount: '',
    bank_charges: '',
    transaction_id: '',
    notes: ''
  });
  const [openBills, setOpenBills] = useState([]);
  const [vendorOutstanding, setVendorOutstanding] = useState(null);
  const [vendorCredit, setVendorCredit] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Search and filter state
  const [filters, setFilters] = useState({
    supplier_name: '',
    start_date: '',
    end_date: '',
    payment_mode: ''
  });

  // Load all payments from all suppliers
  const loadPayments = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      let allPayments = [];
      let totalPayments = 0;

        const params = {
          page,
          per_page: itemsPerPage,
          start_date: filters.start_date,
          end_date: filters.end_date,
          supplier_name: filters.supplier_name,
          payment_mode: filters.payment_mode
        };
        
        const response = await apiService.getSupplierPayments(params);
        allPayments = response.data || [];
        totalPayments = response.pagination?.total || 0;
        setCurrentPage(response.pagination?.page || 1);
        setTotalPages(response.pagination?.pages || 1);

      setData(allPayments);
      setTotalItems(totalPayments);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load supplier payment history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadPayments();
    apiService.getVendors().then(r => setAllVendors(r.data || [])).catch(() => {});
  }, []);

  // Reload when filters change
  useEffect(() => {
    loadPayments(1);
  }, [filters]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      vendor_id: '',
      supplier_name: '',
      bill_no: '',
      goods_purchase_id: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      cash_amount: '',
      bank_amount: '',
      bank_charges: '',
      transaction_id: '',
      notes: ''
    });
    setOpenBills([]);
    setVendorOutstanding(null);
    setVendorCredit(null);
    setCurrentView('form');
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      vendor_id: item.vendor_id ? item.vendor_id.toString() : '',
      supplier_name: item.supplier_name || '',
      bill_no: item.bill_no || '',
      goods_purchase_id: item.goods_purchase_id ? item.goods_purchase_id.toString() : '',
      amount: item.amount?.toString() || '',
      date: item.date || new Date().toISOString().split('T')[0],
      payment_mode: item.payment_mode || 'cash',
      cash_amount: item.cash_amount?.toString() || '',
      bank_amount: (item.bank_amount ?? item.online_amount ?? '').toString(),
      bank_charges: (item.bank_charges ?? '').toString(),
      transaction_id: item.transaction_id || '',
      notes: item.notes || ''
    });
    if (item.vendor_id) {
      try {
        const r = await apiService.getVendorOpenBills(item.vendor_id);
        setOpenBills(r.data || []);
      } catch { setOpenBills([]); }
      try {
        const s = await apiService.getVendorSummary(item.vendor_id);
        setVendorOutstanding(s.data?.outstanding ?? null);
      } catch { setVendorOutstanding(null); }
      try {
        const c = await apiService.getVendorCredit(item.vendor_id);
        setVendorCredit(c.data?.available ?? 0);
      } catch { setVendorCredit(null); }
    } else {
      setVendorOutstanding(null);
      setVendorCredit(null);
    }
    setCurrentView('form');
  };

  const handleDelete = async (paymentId) => {
    if (window.confirm('Are you sure you want to delete this payment record?')) {
      try {
        setLoading(true);
        await apiService.deleteSupplierPayment(paymentId);
        await loadPayments(currentPage);
      } catch (err) {
        console.error('Error deleting payment:', err);
        toast.error('Failed to delete payment: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.vendor_id && !formData.supplier_name.trim()) {
      toast.warning('Please select a vendor or enter a supplier name.');
      return;
    }

    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
      toast.warning('Please enter a valid payment amount.');
      return;
    }

    if (!formData.date) {
      toast.warning('Please select a payment date.');
      return;
    }

    const totalAmount = Number(formData.amount);
    const cashAmount = formData.cash_amount ? Number(formData.cash_amount) : 0;
    const bankAmount = formData.bank_amount ? Number(formData.bank_amount) : 0;

    if (formData.payment_mode === 'both' && Math.abs((cashAmount + bankAmount) - totalAmount) > 0.01) {
      toast.warning('Cash + Bank must equal total amount when mode is "Both".');
      return;
    }

    // Surplus warning: overpay beyond vendor outstanding becomes advance credit
    if (formData.vendor_id && vendorOutstanding != null) {
      const excludeOwn = editingItem && editingItem.vendor_id?.toString() === formData.vendor_id.toString()
        ? Number(editingItem.amount || 0) : 0;
      const allowed = vendorOutstanding + excludeOwn;
      if (totalAmount > allowed + 0.01) {
        const surplus = (totalAmount - allowed).toFixed(2);
        if (!window.confirm(`₹${surplus} will be saved as advance / surplus credit for this vendor. Continue?`)) return;
      }
    }

    const submissionData = {
      vendor_id: formData.vendor_id ? parseInt(formData.vendor_id) : undefined,
      supplier_name: formData.vendor_id ? undefined : formData.supplier_name,
      bill_no: formData.bill_no.trim() || null,
      goods_purchase_id: formData.goods_purchase_id ? parseInt(formData.goods_purchase_id) : null,
      amount: totalAmount,
      date: formData.date,
      payment_mode: formData.payment_mode,
      cash_amount: formData.payment_mode === 'cash' ? totalAmount : (formData.payment_mode === 'both' ? cashAmount : 0),
      bank_amount: (formData.payment_mode === 'bank' || formData.payment_mode === 'upi') ? totalAmount : (formData.payment_mode === 'both' ? bankAmount : 0),
      bank_charges: (formData.payment_mode !== 'cash' && formData.bank_charges) ? Number(formData.bank_charges) : 0,
      transaction_id: formData.transaction_id.trim() || null,
      notes: formData.notes.trim() || null
    };

    try {
      setLoading(true);
      setError(null);

      if (editingItem) {
        await apiService.updateSupplierPayment(editingItem.payment_id, submissionData);
      } else {
        await apiService.createSupplierPayment(submissionData);
      }
      
      await loadPayments(currentPage);
      setCurrentView('list');
      setFormData({
        vendor_id: '',
        supplier_name: '',
        bill_no: '',
        goods_purchase_id: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        cash_amount: '',
        bank_amount: '',
        bank_charges: '',
        transaction_id: '',
        notes: ''
      });
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving payment:', err);
      toast.error('Failed to save payment: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'payment_mode') {
      if (value === 'cash') {
        setFormData(prev => ({ ...prev, [name]: value, cash_amount: prev.amount, bank_amount: '', bank_charges: '' }));
      } else if (value === 'bank' || value === 'upi') {
        setFormData(prev => ({ ...prev, [name]: value, cash_amount: '', bank_amount: prev.amount }));
      } else if (value === 'both') {
        setFormData(prev => ({ ...prev, [name]: value, cash_amount: '', bank_amount: '' }));
      }
    }

    if (name === 'amount') {
      if (formData.payment_mode === 'cash') {
        setFormData(prev => ({ ...prev, [name]: value, cash_amount: value }));
      } else if (formData.payment_mode === 'bank' || formData.payment_mode === 'upi') {
        setFormData(prev => ({ ...prev, [name]: value, bank_amount: value }));
      }
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingItem(null);
    setError(null);
  };

  const handlePageChange = (page) => {
    loadPayments(page);
  };

  const clearFilters = () => {
    setFilters({
      supplier_name: '',
      start_date: '',
      end_date: '',
      payment_mode: ''
    });
  };

  const formatCurrency = (amount) => {
    return amount ? `₹${parseFloat(amount).toFixed(2)}` : '₹0.00';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  // Show loading spinner
  if (loading && data.length === 0) {
    return renderLayout(
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading payment history...</p>
        </div>
    );
  }

  // Form View
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
               {editingItem ? 'Edit Payment Record' : 'Add New Payment'}
            </h1>
           </div>
        </div>

        {error && (
            <div className="error-container">
                <p>{error}</p>
            </div>
        )}

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Vendor</label>
                  <select
                    name="vendor_id"
                    className="form-select"
                    value={formData.vendor_id}
                    onChange={async (e) => {
                      const vendorId = e.target.value;
                      const vendor = allVendors.find(v => v.vendor_id.toString() === vendorId);
                      setFormData(prev => ({
                        ...prev,
                        vendor_id: vendorId,
                        supplier_name: vendor ? vendor.name : prev.supplier_name,
                        bill_no: '',
                        goods_purchase_id: ''
                      }));
                      if (vendorId) {
                        try {
                          const r = await apiService.getVendorOpenBills(parseInt(vendorId));
                          setOpenBills(r.data || []);
                        } catch { setOpenBills([]); }
                        try {
                          const s = await apiService.getVendorSummary(parseInt(vendorId));
                          setVendorOutstanding(s.data?.outstanding ?? null);
                        } catch { setVendorOutstanding(null); }
                        try {
                          const c = await apiService.getVendorCredit(parseInt(vendorId));
                          setVendorCredit(c.data?.available ?? 0);
                        } catch { setVendorCredit(null); }
                      } else {
                        setOpenBills([]);
                        setVendorOutstanding(null);
                        setVendorCredit(null);
                      }
                    }}
                    disabled={loading}
                  >
                    <option value="">-- Select Vendor (optional) --</option>
                    {sortedByName(allVendors).map(v => (
                      <option key={v.vendor_id} value={v.vendor_id}>
                        {v.name} ({v.vendor_type})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="date" className="form-label">Payment Date *</label>
                  <input
                    type="date" max={TODAY_DATE}
                    id="date"
                    name="date"
                    className="form-input"
                    value={formData.date}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
            </div>
            {formData.vendor_id && openBills.length > 0 && (
              <div className="form-group">
                <label className="form-label">Against Bill (optional — auto-caps amount)</label>
                <select
                  className="form-select"
                  value={formData.goods_purchase_id}
                  onChange={(e) => {
                    const gpId = e.target.value;
                    const b = openBills.find(b => b.purchase_id.toString() === gpId);
                    setFormData(prev => ({
                      ...prev,
                      goods_purchase_id: gpId,
                      bill_no: b ? (b.bill_no || '') : prev.bill_no,
                      amount: b ? b.balance.toString() : prev.amount
                    }));
                  }}
                >
                  <option value="">-- Select a bill (optional) --</option>
                  {openBills.map(b => (
                    <option key={b.purchase_id} value={b.purchase_id}>
                      {b.bill_no || `GP-${b.purchase_id}`} | {b.product_name} | Bal: ₹{b.balance.toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Vendor Bill No (manual)</label>
              <input type="text" name="bill_no" className="form-input" value={formData.bill_no} onChange={handleInputChange} placeholder="Bill no. as on vendor's invoice" />
            </div>

            {!formData.vendor_id && (
              <div className="form-group">
                <label htmlFor="supplier_name" className="form-label">Supplier Name *</label>
                <input
                  type="text"
                  id="supplier_name"
                  name="supplier_name"
                  className="form-input"
                  value={formData.supplier_name}
                  onChange={handleInputChange}
                  placeholder="Enter supplier name (if not in vendor list)"
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="amount" className="form-label">Total Amount *</label>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        className="form-input"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="Enter total amount"
                        min="0"
                        step="0.01"
                        disabled={loading}
                    />
                    {formData.vendor_id && vendorOutstanding != null && (() => {
                      const excludeOwn = editingItem && editingItem.vendor_id?.toString() === formData.vendor_id.toString()
                        ? Number(editingItem.amount || 0) : 0;
                      const allowed = vendorOutstanding + excludeOwn;
                      const total = Number(formData.amount || 0);
                      const overflow = total - allowed;
                      return (
                        <small style={{ color: overflow > 0.01 ? 'var(--warning-color)' : 'var(--text-secondary)' }}>
                          Vendor outstanding: ₹{allowed.toFixed(2)}
                          {overflow > 0.01 && <> &middot; ₹{overflow.toFixed(2)} will go to advance credit</>}
                        </small>
                      );
                    })()}
                    {vendorCredit != null && vendorCredit > 0.01 && (
                      <small style={{ color: 'var(--success-color)', display: 'block' }}>
                        Available advance credit: ₹{vendorCredit.toFixed(2)}
                      </small>
                    )}
                  </div>
                   <div className="form-group">
                    <label htmlFor="payment_mode" className="form-label">Payment Mode *</label>
                    <select
                        id="payment_mode"
                        name="payment_mode"
                        className="form-select"
                        value={formData.payment_mode}
                        onChange={handleInputChange}
                        disabled={loading}
                    >
                        <option value="cash">Cash</option>
                        <option value="bank">Bank</option>
                        <option value="upi">UPI</option>
                        <option value="both">Both (Cash + Bank)</option>
                    </select>
                    </div>
            </div>

            {formData.payment_mode === 'both' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cash Portion</label>
                  <input type="number" name="cash_amount" className="form-input" value={formData.cash_amount} onChange={handleInputChange} min="0" step="0.01" disabled={loading} />
                </div>
                <div className="form-group">
                  <label className="form-label">Bank Portion</label>
                  <input type="number" name="bank_amount" className="form-input" value={formData.bank_amount} onChange={handleInputChange} min="0" step="0.01" disabled={loading} />
                </div>
              </div>
            )}

            {formData.payment_mode !== 'cash' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Bank Charges (optional)</label>
                  <input type="number" name="bank_charges" className="form-input" value={formData.bank_charges} onChange={handleInputChange} placeholder="0.00" min="0" step="0.01" disabled={loading} />
                  <small style={{ color: 'var(--text-secondary)' }}>Extra charges (RTGS/IMPS, etc.). Leave blank if none.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="transaction_id" className="form-label">Transaction ID</label>
                  <input type="text" id="transaction_id" name="transaction_id" className="form-input" value={formData.transaction_id} onChange={handleInputChange} placeholder="Enter transaction ID" disabled={loading} />
                </div>
              </div>
            )}

             <div className="form-group">
                  <label htmlFor="notes" className="form-label">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    className="form-textarea"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter any additional notes"
                    rows="3"
                    disabled={loading}
                  />
            </div>

            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                     <Check size={18} />
                    {loading ? 'Saving...' : (editingItem ? 'Update Payment' : 'Add Payment')}
                </button>
            </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">
          Payment History
        </h1>
        <button className="btn btn-primary" onClick={handleAdd} disabled={loading}>
          <Plus size={18} /> Add Payment
        </button>
      </div>

       {error && (
        <div className="error-container">
            <p>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={() => loadPayments(currentPage)}>Retry</button>
        </div>
      )}

      <div className="filter-bar">
         <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Supplier</label>
            <input
                  type="text"
                  name="supplier_name"
                  value={filters.supplier_name}
                  onChange={handleFilterChange}
                  className="form-input"
                  placeholder="Supplier Name"
            />
        </div>
        <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Start Date</label>
            <input
                  type="date" max={TODAY_DATE}
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="form-input"
            />
        </div>
        <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>End Date</label>
            <input
                  type="date" max={TODAY_DATE}
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="form-input"
            />
        </div>
         <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Mode</label>
             <select
                  name="payment_mode"
                  value={filters.payment_mode}
                  onChange={handleFilterChange}
                  className="form-select"
                >
          <option value="">All Modes</option>
          <option value="cash">Cash</option>
          <option value="bank">Bank</option>
          <option value="upi">UPI</option>
          <option value="both">Both</option>
        </select>
        </div>
        <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px', alignSelf: 'flex-end' }}>
          Clear
        </button>
      </div>

       <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Bill No</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Cash</th>
                  <th>Bank</th>
                  <th>Transaction ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(item => (
                  <tr key={`payment-${item.payment_id}`}>
                    <td>#{item.payment_id}</td>
                    <td>{formatDate(item.date)}</td>
                    <td style={{ fontWeight: 500 }}>
                      {item.supplier_name}
                      {item.vendor_id && <span style={{ fontSize: '0.75rem', color: 'var(--primary-color)', marginLeft: '4px' }}>(V)</span>}
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--primary-color)' }}>{item.bill_no || '—'}</td>
                    <td style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatCurrency(item.amount)}</td>
                    <td>
                      <span className={`mode-badge ${item.payment_mode}`}>
                        {item.payment_mode}
                      </span>
                    </td>
                    <td>{item.cash_amount ? formatCurrency(item.cash_amount) : '-'}</td>
                    <td>{(item.bank_amount ?? item.online_amount) ? formatCurrency(item.bank_amount ?? item.online_amount) : '-'}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {item.transaction_id || '-'}
                    </td>
                    <td>
                        <div className="actions-cell">
                        <button 
                          className="btn-icon"
                          onClick={() => handleEdit(item)}
                          disabled={loading}
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          className="btn-icon"
                          style={{ color: 'var(--error-color)' }}
                          onClick={() => handleDelete(item.payment_id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
           {data.length === 0 && !loading && (
            <div className="no-data">
                <p>No payment records found</p>
            </div>
            )}
        </div>

        {totalPages > 1 && (
        <div className="pagination">
            <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            >
            <ChevronLeft size={16} />
            </button>
            
             <span style={{ margin: '0 1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                Page {currentPage} of {totalPages}
              </span>
            
            <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            >
            <ChevronRight size={16} />
            </button>
        </div>
        )}
        
        <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Records (this page)</div>
            <div style={{ fontWeight: 700 }}>{data.length}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Sum (this page)</div>
            <div style={{ fontWeight: 700, color: 'var(--success-color)' }}>{formatCurrency(data.reduce((s, p) => s + (p.amount || 0), 0))}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Records (filter total)</div>
            <div style={{ fontWeight: 700 }}>{totalItems}</div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
             Showing {data.length} of {totalItems} records
        </div>
    </>
  );
}
export default SupplierPaymentPage;